import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  FilterSelect,
  SearchInput,
  StyledPagination,
  EmptyState,
  StatusChip
} from '../components/TableShared';
import { format } from 'date-fns';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

const TRANSACTION_TYPES = ['All', 'PURCHASE', 'SPEND', 'BONUS'];

const TYPE_CHIP_MAP = {
  PURCHASE: { color: '#2B6CB0', bgColor: '#EBF8FF' },
  SPEND: { color: '#C53030', bgColor: '#FFF5F5' },
  BONUS: { color: '#276749', bgColor: '#F0FFF4' },
};

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [typeFilter, setTypeFilter] = useState('All');
  const [search, setSearch] = useState('');

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (typeFilter !== 'All') {
        params.type = typeFilter;
      }
      if (search) {
        params.search = search;
      }
      const response = await api.get('/admin/transactions', { params });
      const data = response.data.data || response.data;
      setTransactions(data.transactions || []);
      setTotalTransactions(data.total || data.transactions?.length || 0);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to load transactions. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, typeFilter, search]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  const formatAmount = (amount, type) => {
    if (amount == null) return 'N/A';
    const isPositive = type === 'PURCHASE' || type === 'BONUS';
    const prefix = isPositive ? '+' : '-';
    return `${prefix}${Math.abs(amount)}`;
  };

  const getAmountColor = (type) => {
    if (type === 'PURCHASE' || type === 'BONUS') return '#276749';
    if (type === 'SPEND') return '#C53030';
    return '#2D3748';
  };

  return (
    <Box sx={{ fontFamily: ff }}>
      {/* Filter Bar */}
      <FilterBar>
        <SearchInput
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search by user, description..."
        />
        <FilterSelect
          label="Type"
          value={typeFilter}
          onChange={handleTypeFilterChange}
          options={TRANSACTION_TYPES}
        />
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={6} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((txn) => (
                <TableRow key={txn.id}>
                  <TableCell>
                    {txn.id ? `#${String(txn.id).slice(-8)}` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {txn.user
                      ? txn.user.fullName || txn.user.email
                      : txn.userId || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={txn.type || 'N/A'}
                      color={TYPE_CHIP_MAP[txn.type]?.color || '#718096'}
                      bgColor={TYPE_CHIP_MAP[txn.type]?.bgColor || '#F7FAFC'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        color: getAmountColor(txn.type),
                        fontWeight: 600,
                        fontFamily: ff,
                        fontSize: 13,
                      }}
                    >
                      {formatAmount(txn.amount, txn.type)}
                    </Box>
                  </TableCell>
                  <TableCell>{txn.description || 'N/A'}</TableCell>
                  <TableCell>{formatDate(txn.createdAt)}</TableCell>
                </TableRow>
              ))}
              {transactions.length === 0 && (
                <EmptyState colSpan={6} message="No transactions found." />
              )}
            </TableBody>
          </Table>
          <StyledPagination
            count={totalTransactions}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </StyledTableContainer>
      )}
    </Box>
  );
}
